import React from "react";
import { Quote, Star } from "lucide-react";
import { TESTIMONIALS } from "../mock";
import Reveal from "./ui/Reveal";

// Small Google "G" logo built with inline SVG (brand-accurate colors).
const GoogleG = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M46 24c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.4-4.7 7v5.8h7.6C43.8 37.3 46 31.2 46 24z" />
    <path fill="#34A853" d="M24 46c6.3 0 11.6-2.1 15.4-5.7l-7.6-5.8c-2.1 1.4-4.8 2.2-7.8 2.2-6 0-11.1-4-12.9-9.5H3.3v5.9C7.1 40.9 15 46 24 46z" />
    <path fill="#FBBC05" d="M11.1 27.2c-.5-1.4-.7-2.9-.7-4.2s.3-2.8.7-4.2v-5.9H3.3C1.8 15.9 1 19.4 1 23s.8 7.1 2.3 10.1l7.8-5.9z" />
    <path fill="#EA4335" d="M24 10.5c3.4 0 6.5 1.2 8.9 3.5l6.7-6.7C35.6 3.5 30.3 1 24 1 15 1 7.1 6.1 3.3 13.9l7.8 5.9C12.9 14.5 17.9 10.5 24 10.5z" />
  </svg>
);

const Stars = () => (
  <div className="flex items-center gap-0.5" aria-label="5 stelle su 5">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={14} className="fill-[#FBBC05] text-[#FBBC05]" />
    ))}
  </div>
);

export default function Testimonials() {
  const avgRating = "5.0";
  const totalReviews = TESTIMONIALS.length;

  return (
    <section id="recensioni" className="py-24 lg:py-36 bg-[#0c0c0c] border-y border-white/5">
      <div className="container-max">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <span className="section-label">Testimonials</span>
            <Reveal
              variant="zoom"
              as="h2"
              className="font-display text-4xl lg:text-6xl leading-[1] text-white mt-6 max-w-3xl"
            >
              Chi lo ha vissuto,<br />
              <span className="italic font-light text-white/60">lo racconta.</span>
            </Reveal>
          </div>

          {/* Google verified badge */}
          <a
            href="https://www.google.com/search?q=Digital+Enterprise+Formula+Pietrasanta"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 hover:bg-white/[0.06] transition-colors"
            data-testid="testimonials-google-badge"
          >
            <GoogleG size={18} />
            <div className="flex items-center gap-2">
              <Stars />
              <span className="text-white/90 text-sm font-medium">{avgRating}</span>
            </div>
            <span className="text-white/40 text-xs">
              · {totalReviews}+ recensioni Google
            </span>
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <Reveal
              key={i}
              variant="fade-up"
              delay={i * 80}
              className="bg-card border border-white/10 rounded-2xl p-7 lg:p-8 hover-lift flex flex-col"
              data-testid={`testimonial-card-${i}`}
            >
              <div className="flex items-start justify-between">
                <Quote size={26} style={{ color: "#d4ff3d" }} />
                <Stars />
              </div>

              <p className="mt-5 text-white/85 text-base lg:text-lg leading-relaxed flex-1">
                {t.text}
              </p>

              <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-display text-white text-sm">
                    {t.name
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-white text-sm truncate">
                    {t.name}
                  </div>
                  <div className="text-white/50 text-xs mt-0.5 truncate">
                    {t.role}
                  </div>
                </div>
                {t.verified && (
                  <div className="flex items-center gap-1 text-[10px] text-white/40 whitespace-nowrap">
                    <GoogleG size={11} />
                    <span>verificata</span>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="https://www.google.com/search?q=Digital+Enterprise+Formula+Pietrasanta"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm underline underline-offset-4 decoration-white/20 hover:decoration-white/60 transition-colors"
            data-testid="testimonials-see-all-link"
          >
            Leggi tutte le recensioni su Google →
          </a>
        </div>
      </div>
    </section>
  );
}
